import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { requiredText } from '../../../lib/forms/rules'
import ActionPeopleEditor from '../../actions/components/ActionPeopleEditor'
import ManagerListPicker from '../../managerLists/components/ManagerListPicker'
import type { ManagerList } from '../../managerLists/types'
import type { MinutePerson, PersonInput, PrincipleDecision, PrincipleDecisionUpsert } from '../types'
import { toInput, toPerson } from '../types'
import { COLUMN_BYTES } from '../../../lib/columnBytes'

/**
 * Prensip Kararı metni **2000** karakter (kullanıcı kararı 2026-08-19; backend
 * `MinuteItemLimits.PRINCIPLE_NAME_MAX` ile birebir). Bu ekran toplantıya bağlı prensipleri de düzenler,
 * dolayısıyla sınırı Tutanak Oluştur / Toplantı Detay ile **aynı** olmak zorundadır — aksi halde aynı
 * kayıt bir ekrandan kaydedilip diğerinden reddedilirdi.
 */
const DESCRIPTION_MAX = 2000

const schema = z.object({
  description: requiredText(DESCRIPTION_MAX, COLUMN_BYTES.meetingMinutesText),
})

interface Props {
  visible: boolean
  /** Düzenlenen kayıt; null → yeni oluşturma. */
  initial: PrincipleDecision | null
  submitting: boolean
  onSubmit: (payload: PrincipleDecisionUpsert) => void
  onHide: () => void
}

/**
 * Prensip Kararı oluştur/düzenle (analiz 5.4.1/5.4.4). <b>Açıklama</b> (çok satırlı, RHF+zod) ve
 * <b>en az bir Sorumlu</b> zorunludur (2026-08-12; sorumlu önceden tekil ve opsiyoneldi — bu modül
 * toplantıya bağlı prensipleri de düzenlediğinden tutanak tarafıyla aynı kurala getirildi).
 * Kişi seçiciler RHF-dışı yerel durumda tutulur (ActionFormDialog kalıbı).
 */
export default function PrincipleDecisionFormDialog({
  visible,
  initial,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useZodForm(schema, {
    defaultValues: { description: '' },
  })
  const [responsibles, setResponsibles] = useState<MinutePerson[]>([])
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (visible) {
      reset({ description: initial?.description ?? '' })
      setResponsibles(initial?.responsibles ?? [])
      setShowErrors(false)
    }
  }, [visible, initial, reset])

  // Sorumlu ekle/çıkar — sicil bazında tekil (mükerrer ekleme yok sayılır; aksiyon formuyla aynı kalıp).
  const addResponsible = (p: PersonInput) =>
    setResponsibles((list) =>
      list.some((x) => x.employeeId === p.employeeId) ? list : [...list, toPerson(p)],
    )

  /**
   * İsim listesiyle toplu ekleme (115): listenin kişileri kümeye katılır ve her biri listenin
   * **etiketini** taşır — tablo bu etikete göre gruplar.
   *
   * <p>Zaten ekli bir sicil **atlanır ve etiketi değişmez**: tek tek eklenmiş bir kişi, listeyi
   * eklemek yüzünden o listenin üyesi gibi görünmemeli. Tersi de geçerli — listeden gelen kişi
   * sonradan tek tek eklenemez zaten (sicil tekil).
   */
  const addList = (ml: ManagerList) =>
    setResponsibles((list) => {
      const existing = new Set(list.map((x) => x.employeeId))
      const added = ml.people
        .filter((p) => !existing.has(p.employeeId))
        .map((p) => ({
          employeeId: p.employeeId,
          fullName: p.fullName || p.employeeId,
          firstName: p.firstName,
          lastName: p.lastName,
          jobTitleTr: p.jobTitleTr,
          jobTitleEn: p.jobTitleEn,
          businessMail: p.businessMail,
          assigneeTypeId: null,
          managerListId: ml.id,
          managerListName: ml.name,
        }))
      return added.length === 0 ? list : [...list, ...added]
    })
  /**
   * Sorumlu çıkar. **Kalanların liste bağı KORUNUR** (kullanıcı kararı 01.10.2026): biri çıkarılsa da
   * ötekilerin o listeden geldiği görünmeye devam etmeli.
   *
   * <p>Aynı gün kısa süre tersi denendi — "liste eksildiyse grup dağılsın, kişi adları görünsün" —
   * ve kullanıcı tarafından geri alındı. Grup, listenin bugünkü hâlini değil **bu kayıtta duran
   * üyeleri** gösterir; nereden geldikleri bilgisi, kaç kişi kaldığından bağımsız olarak değerlidir.
   */
  const removeResponsible = (employeeId: string) =>
    setResponsibles((list) => list.filter((x) => x.employeeId !== employeeId))

  const onValid = (v: { description: string }) => {
    // En az bir Sorumlu zorunlu — RHF/zod dışında tutulduğu için burada elle doğrulanır (sunucu da doğrular).
    if (responsibles.length === 0) {
      setShowErrors(true)
      return
    }
    const payload: PrincipleDecisionUpsert = {
      description: v.description,
      responsibles: responsibles.map(toInput),
    }
    onSubmit(payload)
  }

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('principleDecisions.form.editTitle') : t('principleDecisions.form.newTitle')}
      submitLabel={t('principleDecisions.form.save')}
      submitting={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
      // 56rem (kullanıcı isteği 01.10.2026): açıklama çok satırlı, altında kişi araması + isim
      // listesi seçicisi YAN YANA duruyor ve sorumlu çipleri sarıyor — 42rem'de hepsi sıkışıyordu.
      width="56rem"
    >
      <div className="admin-form">
        {/* Kayıt grubu (122) formda YOK (analist, 19.10.2026): yeni bağımsız prensibin grubunu sunucu
            oluşturanın Grup Yönetimi üyeliğinden türetir; düzenleme grubu değiştirmez. */}
        <FormTextareaField
          control={control}
          name="description"
          label={t('principleDecisions.form.description')}
          placeholder={t('principleDecisions.form.descriptionPlaceholder')}
          required
          rows={4}
          maxLength={DESCRIPTION_MAX}
        />

        <div className="pd-person-field">
          <span className="pd-person-label">
            {t('principleDecisions.form.responsible')} <span className="ui-req">*</span>
          </span>
          {/* Liste seçicisi kişi aramasının YANINDA (kullanıcı isteği 01.10.2026): ikisi de
              "sorumlu ekleme" eylemi; alt alta dururken liste kutusu ilgisiz bir alan gibi
              görünüyordu. */}
          <ActionPeopleEditor
            people={responsibles}
            editable
            onAdd={addResponsible}
            onRemove={removeResponsible}
            placeholder={t('principleDecisions.form.personPlaceholder')}
            removeLabel={t('principleDecisions.form.removePerson')}
            searchAddon={
              <ManagerListPicker
                onPick={addList}
                placeholder={t('principleDecisions.form.listPlaceholder')}
              />
            }
          />
          {showErrors && responsibles.length === 0 && (
            <span className="act-form-err">{t('principleDecisions.form.responsibleRequired')}</span>
          )}
        </div>
      </div>
    </FormDialog>
  )
}
